import React from 'react';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { Page } from '../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { getAdjustedBreadcrumbPathItems } from '@shared/services/breadcrumbService';
import { userInfo } from '@apollo-internal/cache';
import { useQuery, useReactiveVar } from '@apollo/client';
import GET_AGGREGATE_PERFORMANCE_REPORT from '../queries/getAggregatePerformanceReport';
import AggregatePerformanceGraph from './AggregatePerformanceGraph';
import ApolloError from '@shared/components/ApolloError';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import AggregatePerformanceInfoBox from './AggregatePerformanceInfoBox';
import { AggregatePerformanceMappers } from '../mappers/AggregatePerformanceMappers';
import PdfCsvDownloadButton from '@shared/components/PdfCsvDownloadButton';
import { DATA_DOWNLOAD_TYPES } from '@shared/constants/DataDownloadTypes';
import PrivilegeNames from '../../../constants/privilegeNames';
import NotEnoughDataMessage from '@shared/components/NotEnoughDataMessage';

const TITLE = 'Aggregate Performance Report';
const DESCRIPTION =
  'Performance metrics that are most commonly reported across industry, ' +
  'represented graphically over all time submitted. ' +
  'Additional information for each metric can be found to the left of each graph.';

const BREADCRUMB_PATH_ITEMS = [
  { name: 'Data Reports', url: '/data-reports' },
  { name: 'Industry Reports', url: '/data-reports/industry-reports' },
  { name: TITLE, url: '' }
];

const AggregatePerformanceReport = () => {
  const currentUserInfo = useReactiveVar(userInfo);

  const canDownloadAggregatePerformanceReport = currentUserInfo.Privileges.includes(
    PrivilegeNames.AggregatePerformanceReport_Download
  );

  const {
    data: aggregatePerformanceReportDate,
    loading: aggregatePerformanceReportLoading,
    error: aggregatePerformanceReportError
  } = useQuery(GET_AGGREGATE_PERFORMANCE_REPORT);

  const adjustedBreadcrumbPathItems = getAdjustedBreadcrumbPathItems(
    BREADCRUMB_PATH_ITEMS,
    currentUserInfo.SystemRole.Id
  );

  const classificationMetricsReportData =
    aggregatePerformanceReportDate?.aggregatePerformanceReport?.map(
      AggregatePerformanceMappers.toGraphData
    );

  if (aggregatePerformanceReportError) {
    return <ApolloError />;
  }

  return (
    <Page title={TITLE} className='aggregate-performance-page'>
      <Breadcrumb pathItems={adjustedBreadcrumbPathItems}>
        {canDownloadAggregatePerformanceReport && (
          <PdfCsvDownloadButton dataDownloadType={DATA_DOWNLOAD_TYPES.aggregatePerformanceReport} />
        )}
      </Breadcrumb>
      <PageInstructions title={TITLE} description={DESCRIPTION} />
      <SkeletonWrapper
        skeletonClassName='graph-skeleton-wrapper'
        show={aggregatePerformanceReportLoading}>
        {classificationMetricsReportData?.length === 0 && <NotEnoughDataMessage />}
        {classificationMetricsReportData?.map(metric => (
          <div key={metric.classificationId} className='aggregate-performance-metric-row'>
            <AggregatePerformanceInfoBox classificationMetric={metric} />
            <AggregatePerformanceGraph classificationMetric={metric} />
          </div>
        ))}
      </SkeletonWrapper>
    </Page>
  );
};

export default AggregatePerformanceReport;
